/* global gtag */
import React, { useCallback, useEffect } from 'react';
import _capitalize from 'lodash/capitalize';
import { useDispatch, useMappedState } from 'redux-react-hook';
import {
  updateDevLogin, goToNHSStubLogin, submitDevLogin, logOut,
} from 'redux/actions/login';
import { getNextQuestionAfterLogin } from 'redux/actions/consultation';
import LoginProvider from 'styles/blocks/login';
import { SecondaryButton } from 'styles/elements/buttons/primaryCare';
import { STRINGS } from 'config';
import { NHSLogoAndTextSVG } from 'assets/nhs/nhsLoginButton';
import media from 'styles/media';
import { NextButton } from '../nextButton';

const ForcedLoginText = ({ FEDERATED_LOGIN_PROVIDER }) => (
  <>
    <LoginProvider.Text>Please log in with your NHS details to save time on filling in some personal information.</LoginProvider.Text>
    <LoginProvider.List>
      <LoginProvider.ListItem>
        {FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME} is the same login you use for the NHS app.
      </LoginProvider.ListItem>
      <LoginProvider.ListItem isLastListItem>
        If you do not have, or cannot remember your {FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME} details please do not call the service, visit&nbsp;
        <LoginProvider.Link target="_blank" rel="noopener noreferrer" href="https://help.login.nhs.uk/">NHS login Help Centre</LoginProvider.Link>
        .
      </LoginProvider.ListItem>
    </LoginProvider.List>
  </>
);

const NormalLoginPreButtonText = ({ FEDERATED_LOGIN_PROVIDER }) => (
  <LoginProvider.Text>
    If you have an {FEDERATED_LOGIN_PROVIDER.DISPLAY_NAME} (the same as for the NHS app), use it now to save time.
  </LoginProvider.Text>
);

const ContinueAsGuestOption = ({ onContinueAsGuest }) => (
  <>
    <LoginProvider.Text>
      Don&apos;t have one? Continue without it and you can register after submitting your request.
    </LoginProvider.Text>
    <SecondaryButton onClick={onContinueAsGuest}>Continue without logging in</SecondaryButton>
  </>
);

export function Login({ onClickNextQuestion }) {
  const isMobile = media.mobile;
  const mapState = useCallback(state => ({
    devNHSLogin: state.loginReducer.devNHSLogin,
    federatedLoginProvider: state.consultationReducer.federatedLoginProvider,
    loginDetails: state.consultationReducer.loginDetails,
    treatmentCategory: state.consultationReducer.treatmentCategory,
    responseType: state.consultationReducer.responseType,
    product: state.consultationReducer.product,
    forceNhsLoginMode: state.flagsReducer.forceNhsLoginMode,
  }), []);
  const {
    devNHSLogin, federatedLoginProvider, loginDetails, treatmentCategory, responseType, product, forceNhsLoginMode,
  } = useMappedState(mapState);

  const sendGAVirtualPageView = useCallback(loginOption => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'virtual_page_view', {
        event_category: `/virtual/consultation/qpp/${treatmentCategory || ''}/${loginOption}`,
        event_label: responseType,
        value: 1,
      });
    }
  }, [treatmentCategory, responseType]);

  // Send /loginOrGuest virtual pageview on render of this question.
  useEffect(() => {
    sendGAVirtualPageView('loginOrGuest');
  }, [sendGAVirtualPageView]);

  // Create actions
  const dispatch = useDispatch();

  const FEDERATED_LOGIN_PROVIDER = STRINGS.FEDERATED_LOGIN_PROVIDER[federatedLoginProvider] || {};
  const { DEV_LOGIN_ENABLED } = STRINGS;

  const handleContinueAsGuest = () => {
    sendGAVirtualPageView(`${federatedLoginProvider}/continueAsGuest`);
    onClickNextQuestion();
  };

  if (loginDetails.isLoggedIn) {
    const { firstName, surname } = loginDetails.patientDetails || {};
    const journeyType = product === STRINGS.PRODUCT.E_SPECIALIST ? 'questionnaire' : 'consultation';
    return (
      <LoginProvider>
        <LoginProvider.Header> You are currently logged in as {_capitalize(firstName)}. What would you like to do next?</LoginProvider.Header>
        <LoginProvider.Text>You are logged in as <strong>{_capitalize(firstName)} {_capitalize(surname)}</strong>.</LoginProvider.Text>
        <LoginProvider.Text>You can either continue completing your {journeyType} with these details or log out and start over.</LoginProvider.Text>
        <LoginProvider.Confirm onClick={() => dispatch(getNextQuestionAfterLogin(loginDetails))}>Continue with my current login details</LoginProvider.Confirm>
        <LoginProvider.Cancel onClick={() => dispatch(logOut())}>Log out and start over</LoginProvider.Cancel>
      </LoginProvider>
    );
  }

  return (
    <LoginProvider>
      {forceNhsLoginMode
        ? <ForcedLoginText FEDERATED_LOGIN_PROVIDER={FEDERATED_LOGIN_PROVIDER} />
        : <NormalLoginPreButtonText FEDERATED_LOGIN_PROVIDER={FEDERATED_LOGIN_PROVIDER} />}

      {FEDERATED_LOGIN_PROVIDER === STRINGS.FEDERATED_LOGIN_PROVIDER.NHS_LOGIN_STUB
        ? (
          <LoginProvider.NHSLoginButton onClick={() => dispatch(goToNHSStubLogin())}><NHSLogoAndTextSVG isMobile={isMobile} /></LoginProvider.NHSLoginButton>
        )
        : (
          <LoginProvider.LinkToNHSLogin href={FEDERATED_LOGIN_PROVIDER.REDIRECT_URL} onClick={() => sendGAVirtualPageView(federatedLoginProvider)}>
            <NHSLogoAndTextSVG isMobile={isMobile} />
          </LoginProvider.LinkToNHSLogin>
        )}

      {!forceNhsLoginMode && <ContinueAsGuestOption onContinueAsGuest={handleContinueAsGuest}>Continue without logging in</ContinueAsGuestOption>}

      {DEV_LOGIN_ENABLED
      && (
      <LoginProvider>
        <LoginProvider.Textarea aria-label="dev log in details" value={devNHSLogin} onChange={event => dispatch(updateDevLogin(event.target.value))} />
        <NextButton onClick={() => dispatch(submitDevLogin(devNHSLogin))}>Dev login</NextButton>
      </LoginProvider>
      )}

      {!forceNhsLoginMode
      && (
      <LoginProvider.SubText>
        If there are issues with NHS login please do not call the practice, visit&nbsp;
        <LoginProvider.Link target="_blank" rel="noopener noreferrer" href="https://help.login.nhs.uk/">NHS login Help Centre</LoginProvider.Link>
      </LoginProvider.SubText>
      )}
    </LoginProvider>
  );
}
